import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch,
  runTransaction,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../firebase';
import { logger } from '../utils/logger';
import {
  transformBillDocument,
  sortBillsByRoomNumber,
  sortBillsByPeriodDesc
} from '../utils/billingDataTransform';
import {
  VAT_RATES,
  AREA_CONTRIBUTION_RATIOS,
  ELECTRICITY_FUND,
  ROUNDING_UNITS,
  SMS_AUTH,
  DEFAULTS,
  QUERY_LIMITS,
  roundElectricityFee,
  roundWaterFee,
  roundElectricityFund,
  roundOffsetFee,
  calculateVAT,
  calculatePercentage,
  generateSMSCode,
  getSMSExpiryTime
} from '../constants/billing';
import {
  PortvilleBill,
  PortvilleBillingSettings,
  PortvilleUsageRecord,
  PortvilleBuildingUsageRecord,
  PortvilleBillingCalculation,
  PortvilleBillingAuthToken,
  BillingSettingsFormData,
  BillGenerationRequest,
  UsageRecordFormData,
  BuildingUsageRecordFormData,
  SmsAuthRequest,
  SmsAuthVerification,
  BillingViewData,
  BillingStatistics,
  MonthlyBillingStats,
  BillCalculationInput,
  BillGenerationError,
  SmsAuthError,
  BillingError,
  RoomBillingCalculation,
  PortvilleBillingSettingsHistory,
  PortvilleResidencyChange,
  PortvilleBillingSettingsSnapshot
} from '../types/portvilleBilling';

/**
 * 포트빌 관리비 청구 시스템 서비스 클래스
 */
export class PortvilleBillingService {
  private static instance: PortvilleBillingService;

  public static getInstance(): PortvilleBillingService {
    if (!PortvilleBillingService.instance) {
      PortvilleBillingService.instance = new PortvilleBillingService();
    }
    return PortvilleBillingService.instance;
  }

  // ==================== 관리비 설정 관리 ====================

  /**
   * 호실별 관리비 설정 조회
   */
  async getBillingSettings(roomId: string): Promise<PortvilleBillingSettings | null> {
    try {
      logger.debug(`getBillingSettings 호출 - roomId: ${roomId}`);
      
      // Firestore 쿼리 - 클라이언트 사이드 필터링으로 변경
      const q = query(
        collection(db, 'portville_billing_settings'),
        where('roomId', '==', roomId)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        logger.debug(`getBillingSettings - roomId ${roomId}에 대한 설정이 없음`);
        return null;
      }
      
      logger.debug(`getBillingSettings - 총 ${snapshot.docs.length}개의 설정 문서 발견`);
      
      // 현재 날짜
      const now = new Date();
      
      // 유효한 설정들을 필터링 (시작일 <= 현재 <= 종료일 or 종료일이 없음)
      const validSettings = snapshot.docs
        .map(doc => {
          const docData = doc.data();
          const data = {
            id: doc.id,
            ...docData,
            effectiveFrom: docData.effectiveFrom.toDate(),
            effectiveTo: docData.effectiveTo?.toDate(),
            createdAt: docData.createdAt.toDate(),
            updatedAt: docData.updatedAt.toDate()
          } as any;
          
          logger.debug(`getBillingSettings - 설정 문서 ${doc.id}:`, {
            roomId: data.roomId,
            roomSettings: data.roomSettings,
            effectiveFrom: data.effectiveFrom,
            effectiveTo: data.effectiveTo
          });
          
          return data;
        })
        .filter((settings: any) => {
          const startDate = settings.effectiveFrom;
          const endDate = settings.effectiveTo;
          
          // 시작일이 현재보다 미래면 제외
          if (startDate > now) return false;
          
          // 종료일이 없으면 (영구 적용) 포함
          if (!endDate) return true;
          
          // 종료일이 현재보다 과거면 제외
          return endDate >= now;
        })
        .sort((a: any, b: any) => b.effectiveFrom.getTime() - a.effectiveFrom.getTime()); // 최신순 정렬
      
      if (validSettings.length === 0) {
        logger.debug(`getBillingSettings - roomId ${roomId}에 대한 유효한 설정이 없음`);
        return null;
      }
      
      const finalSetting = validSettings[0] as PortvilleBillingSettings;
      logger.debug(`getBillingSettings - 최종 선택된 설정:`, {
        id: finalSetting.id,
        roomSettings: finalSetting.roomSettings
      });
      
      return finalSetting;
    } catch (error) {
      logger.error('관리비 설정 조회 실패:', error);
      throw new BillingError('관리비 설정 조회에 실패했습니다.', 'GET_SETTINGS_ERROR', error);
    }
  }

  /**
   * 모든 호실의 관리비 설정 조회
   */
  async getAllBillingSettings(): Promise<PortvilleBillingSettings[]> {
    try {
      const q = query(
        collection(db, 'portville_billing_settings'),
        orderBy('roomNumber')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          effectiveFrom: data.effectiveFrom.toDate(),
          effectiveTo: data.effectiveTo?.toDate(),
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate()
        } as PortvilleBillingSettings;
      });
    } catch (error) {
      logger.error('전체 관리비 설정 조회 실패:', error);
      throw new BillingError('관리비 설정 조회에 실패했습니다.', 'GET_ALL_SETTINGS_ERROR', error);
    }
  }

  /**
   * 관리비 설정 저장
   */
  async saveBillingSettings(
    formData: BillingSettingsFormData,
    createdBy: string
  ): Promise<string> {
    try {
      const now = Timestamp.now();

      // 기존 설정 조회 및 버전 관리
      const existingSettings = await this.getBillingSettings(formData.roomId);
      let version = 1;
      let parentSettingsId: string | undefined;

      // Firestore 트랜잭션으로 원자적 처리
      const newSettingsId = await runTransaction(db, async (transaction) => {
        // 기존 설정이 있다면 비활성화 및 종료 처리
        if (existingSettings) {
          parentSettingsId = existingSettings.id;
          version = (existingSettings.version || 0) + 1;

          // 기존 설정을 비활성화하고 종료 날짜 설정
          const existingRef = doc(db, 'portville_billing_settings', existingSettings.id);
          transaction.update(existingRef, {
            effectiveTo: Timestamp.fromDate(new Date(formData.effectiveFrom)),
            isActive: false,
            updatedAt: now
          });
        }

        // baseRates 객체 생성
        const baseRates: any = {
          monthlyRent: formData.monthlyRent ?? DEFAULTS.MONTHLY_RENT,
          managementFee: formData.managementFee ?? DEFAULTS.MANAGEMENT_FEE,
          managementFeeVATRate: formData.managementFeeVATRate ?? VAT_RATES.MANAGEMENT_FEE,
          communicationFee: formData.communicationFee ?? DEFAULTS.COMMUNICATION_FEE,
          communicationFeeVATRate: formData.communicationFeeVATRate ?? VAT_RATES.COMMUNICATION_FEE
        };

        // roomSettings 객체 생성
        const roomSettings: any = {
          electricityAreaContributionRatio: formData.electricityAreaContributionRatio ?? AREA_CONTRIBUTION_RATIOS.ELECTRICITY_DEFAULT,
          waterAreaContributionRatio: formData.waterAreaContributionRatio ?? AREA_CONTRIBUTION_RATIOS.WATER_DEFAULT,
          roomType: formData.roomTypeDetail ?? DEFAULTS.ROOM_TYPE
        };

        // 새로운 설정 데이터 생성 (버전 관리 필드 포함)
        const settingsData: any = {
          roomId: formData.roomId,
          roomNumber: formData.roomNumber,
          roomType: formData.roomType || '호실',
          contactInfo: {
            contractorPhoneNumber: formData.contractorPhoneNumber || '',
            roommatePhoneNumber: formData.roommatePhoneNumber || ''
          },
          baseRates,
          roomSettings,
          billingConfig: {
            issueDay: formData.issueDay,
            dueDay: formData.dueDay,
            autoGenerate: formData.autoGenerate
          },
          effectiveFrom: new Date(formData.effectiveFrom),

          // 버전 관리 필드
          version,
          parentSettingsId,
          isActive: true,
          changeReason: formData.changeReason || '설정 업데이트',
          approvedBy: createdBy, // 현재는 생성자가 바로 승인자
          autoApplyOnResidencyChange: formData.autoApplyOnResidencyChange ?? false,
          notificationSent: false,

          createdAt: now.toDate(),
          updatedAt: now.toDate(),
          createdBy
        };

        // effectiveTo는 값이 있을 때만 포함
        if (formData.effectiveTo) {
          settingsData.effectiveTo = new Date(formData.effectiveTo);
        }

        // Firestore 저장용 데이터 (Timestamp 변환)
        const firestoreData: any = {
          ...settingsData,
          effectiveFrom: Timestamp.fromDate(settingsData.effectiveFrom),
          createdAt: now,
          updatedAt: now
        };

        // effectiveTo가 있을 때만 Timestamp로 변환해서 추가
        if (settingsData.effectiveTo) {
          firestoreData.effectiveTo = Timestamp.fromDate(settingsData.effectiveTo);
        }

        // 새로운 설정 생성
        const newSettingsRef = doc(collection(db, 'portville_billing_settings'));
        transaction.set(newSettingsRef, firestoreData);

        return newSettingsRef.id;
      });

      // 히스토리 저장 (트랜잭션 완료 후 별도 실행)
      await this.saveSettingsHistory(
        newSettingsId,
        existingSettings,
        formData,
        createdBy,
        version
      );

      return newSettingsId;
    } catch (error) {
      logger.error('관리비 설정 저장 실패:', error);
      throw new BillingError('관리비 설정 저장에 실패했습니다.', 'SAVE_SETTINGS_ERROR', error);
    }
  }

  /**
   * 설정 변경 히스토리 저장
   */
  private async saveSettingsHistory(
    settingsId: string,
    previousSettings: PortvilleBillingSettings | null,
    formData: BillingSettingsFormData,
    requestedBy: string,
    version: number
  ): Promise<void> {
    try {
      const now = Timestamp.now();

      // 현재 설정 데이터 구성
      const currentSettings: Omit<PortvilleBillingSettings, 'id'> = {
        roomId: formData.roomId,
        roomNumber: formData.roomNumber,
        roomType: formData.roomType || '호실',
        contactInfo: {
          contractorPhoneNumber: formData.contractorPhoneNumber || '',
          roommatePhoneNumber: formData.roommatePhoneNumber || ''
        },
        baseRates: {
          monthlyRent: formData.monthlyRent ?? DEFAULTS.MONTHLY_RENT,
          managementFee: formData.managementFee ?? DEFAULTS.MANAGEMENT_FEE,
          managementFeeVATRate: formData.managementFeeVATRate ?? VAT_RATES.MANAGEMENT_FEE,
          communicationFee: formData.communicationFee ?? DEFAULTS.COMMUNICATION_FEE,
          communicationFeeVATRate: formData.communicationFeeVATRate ?? VAT_RATES.COMMUNICATION_FEE
        },
        roomSettings: {
          electricityAreaContributionRatio: formData.electricityAreaContributionRatio ?? AREA_CONTRIBUTION_RATIOS.ELECTRICITY_DEFAULT,
          waterAreaContributionRatio: formData.waterAreaContributionRatio ?? AREA_CONTRIBUTION_RATIOS.WATER_DEFAULT,
          roomType: formData.roomTypeDetail ?? DEFAULTS.ROOM_TYPE
        },
        billingConfig: {
          issueDay: formData.issueDay,
          dueDay: formData.dueDay,
          autoGenerate: formData.autoGenerate
        },
        effectiveFrom: new Date(formData.effectiveFrom),
        effectiveTo: formData.effectiveTo ? new Date(formData.effectiveTo) : undefined,

        version,
        parentSettingsId: previousSettings?.id,
        isActive: true,
        changeReason: formData.changeReason || '설정 업데이트',
        approvedBy: requestedBy,
        autoApplyOnResidencyChange: formData.autoApplyOnResidencyChange ?? false,
        notificationSent: false,

        createdAt: now.toDate(),
        updatedAt: now.toDate(),
        createdBy: requestedBy
      };

      // 변경된 필드 식별
      const changedFields = this.identifyChangedFields(previousSettings, currentSettings);

      const historyData: Omit<PortvilleBillingSettingsHistory, 'id'> = {
        settingsId,
        version,
        previousSettings: previousSettings ? { ...previousSettings, id: undefined } as Omit<PortvilleBillingSettings, 'id'> : {} as Omit<PortvilleBillingSettings, 'id'>,
        currentSettings,
        changeType: previousSettings ? 'update' : 'create',
        changeReason: formData.changeReason || '설정 업데이트',
        changedFields,
        requestedBy,
        approvedBy: requestedBy, // 현재는 자동 승인
        approvalStatus: 'approved',
        requestedAt: now.toDate(),
        processedAt: now.toDate(),
        createdAt: now.toDate(),
        updatedAt: now.toDate()
      };

      // Timestamp 변환하여 저장
      const firestoreHistoryData = {
        ...historyData,
        requestedAt: now,
        processedAt: now,
        createdAt: now,
        updatedAt: now,
        previousSettings: this.convertDatesToTimestamps(historyData.previousSettings),
        currentSettings: this.convertDatesToTimestamps(historyData.currentSettings)
      };

      await addDoc(collection(db, 'portville_billing_settings_history'), firestoreHistoryData);

      logger.debug('설정 변경 히스토리 저장 완료:', {
        settingsId,
        version,
        changeType: historyData.changeType,
        changedFields: changedFields.length
      });
    } catch (error) {
      logger.error('설정 히스토리 저장 실패:', error);
      // 히스토리 저장 실패가 주요 기능을 막지 않도록 에러를 던지지 않음
    }
  }

  /**
   * 설정 변경된 필드 식별
   */
  private identifyChangedFields(
    previous: PortvilleBillingSettings | null,
    current: Omit<PortvilleBillingSettings, 'id'>
  ): string[] {
    if (!previous) return ['all']; // 새 생성인 경우

    const changedFields: string[] = [];

    // 기본 필드 비교
    if (previous.roomNumber !== current.roomNumber) changedFields.push('roomNumber');
    if (previous.roomType !== current.roomType) changedFields.push('roomType');

    // 연락처 정보 비교
    if (previous.contactInfo?.contractorPhoneNumber !== current.contactInfo?.contractorPhoneNumber) {
      changedFields.push('contactInfo.contractorPhoneNumber');
    }
    if (previous.contactInfo?.roommatePhoneNumber !== current.contactInfo?.roommatePhoneNumber) {
      changedFields.push('contactInfo.roommatePhoneNumber');
    }

    // 기본 요금 비교
    if (previous.baseRates?.monthlyRent !== current.baseRates?.monthlyRent) changedFields.push('baseRates.monthlyRent');
    if (previous.baseRates?.managementFee !== current.baseRates?.managementFee) changedFields.push('baseRates.managementFee');
    if (previous.baseRates?.managementFeeVATRate !== current.baseRates?.managementFeeVATRate) changedFields.push('baseRates.managementFeeVATRate');
    if (previous.baseRates?.communicationFee !== current.baseRates?.communicationFee) changedFields.push('baseRates.communicationFee');
    if (previous.baseRates?.communicationFeeVATRate !== current.baseRates?.communicationFeeVATRate) changedFields.push('baseRates.communicationFeeVATRate');

    // 호실 설정 비교
    if (previous.roomSettings?.electricityAreaContributionRatio !== current.roomSettings?.electricityAreaContributionRatio) {
      changedFields.push('roomSettings.electricityAreaContributionRatio');
    }
    if (previous.roomSettings?.waterAreaContributionRatio !== current.roomSettings?.waterAreaContributionRatio) {
      changedFields.push('roomSettings.waterAreaContributionRatio');
    }

    // 적용 기간 비교
    if (previous.effectiveFrom?.getTime() !== current.effectiveFrom?.getTime()) changedFields.push('effectiveFrom');
    if (previous.effectiveTo?.getTime() !== current.effectiveTo?.getTime()) changedFields.push('effectiveTo');

    return changedFields;
  }

  /**
   * Date 객체를 Timestamp로 변환하는 헬퍼 함수
   */
  private convertDatesToTimestamps(obj: any): any {
    if (!obj) return obj;

    const converted = { ...obj };

    // Date 필드들을 Timestamp로 변환
    if (converted.effectiveFrom instanceof Date) {
      converted.effectiveFrom = Timestamp.fromDate(converted.effectiveFrom);
    }
    if (converted.effectiveTo instanceof Date) {
      converted.effectiveTo = Timestamp.fromDate(converted.effectiveTo);
    }
    if (converted.createdAt instanceof Date) {
      converted.createdAt = Timestamp.fromDate(converted.createdAt);
    }
    if (converted.updatedAt instanceof Date) {
      converted.updatedAt = Timestamp.fromDate(converted.updatedAt);
    }

    return converted;
  }

  // ==================== 시점별 설정 조회 API ====================

  /**
   * 특정 시점의 관리비 설정 조회
   */
  async getBillingSettingsAtDate(
    roomId: string,
    effectiveDate: Date
  ): Promise<PortvilleBillingSettingsSnapshot | null> {
    try {
      logger.debug(`getBillingSettingsAtDate 호출 - roomId: ${roomId}, date: ${effectiveDate}`);

      // 해당 시점에 유효한 설정들을 조회
      const q = query(
        collection(db, 'portville_billing_settings'),
        where('roomId', '==', roomId),
        where('effectiveFrom', '<=', Timestamp.fromDate(effectiveDate)),
        orderBy('effectiveFrom', 'desc'),
        orderBy('version', 'desc'),
        limit(10) // 최신 10개만 조회 후 클라이언트 사이드 필터링
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        logger.debug(`getBillingSettingsAtDate - roomId ${roomId}에 대한 설정이 없음`);
        return null;
      }

      // 조회된 설정들 중에서 해당 시점에 유효한 설정 찾기
      const validSettings = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          effectiveFrom: doc.data().effectiveFrom.toDate(),
          effectiveTo: doc.data().effectiveTo?.toDate(),
          createdAt: doc.data().createdAt.toDate(),
          updatedAt: doc.data().updatedAt.toDate()
        } as PortvilleBillingSettings))
        .filter(settings => {
          const startDate = settings.effectiveFrom;
          const endDate = settings.effectiveTo;

          // 시작일이 기준 날짜보다 미래면 제외
          if (startDate > effectiveDate) return false;

          // 종료일이 없으면 (영구 적용) 포함
          if (!endDate) return true;

          // 종료일이 기준 날짜보다 과거면 제외
          return endDate >= effectiveDate;
        })
        .sort((a, b) => {
          // 시작일 내림차순, 버전 내림차순으로 정렬
          if (a.effectiveFrom.getTime() !== b.effectiveFrom.getTime()) {
            return b.effectiveFrom.getTime() - a.effectiveFrom.getTime();
          }
          return (b.version || 0) - (a.version || 0);
        });

      if (validSettings.length === 0) {
        logger.debug(`getBillingSettingsAtDate - roomId ${roomId}에 대한 유효한 설정이 없음`);
        return null;
      }

      const targetSettings = validSettings[0];

      // 다음 변경 예정일 조회
      const nextChangeQuery = query(
        collection(db, 'portville_billing_settings'),
        where('roomId', '==', roomId),
        where('effectiveFrom', '>', Timestamp.fromDate(effectiveDate)),
        orderBy('effectiveFrom', 'asc'),
        limit(1)
      );

      const nextChangeSnapshot = await getDocs(nextChangeQuery);
      const nextChangeDate = nextChangeSnapshot.empty
        ? undefined
        : nextChangeSnapshot.docs[0].data().effectiveFrom.toDate();

      // 전체 히스토리 개수 조회
      const historyQuery = query(
        collection(db, 'portville_billing_settings_history'),
        where('settingsId', '==', targetSettings.id)
      );
      const historySnapshot = await getDocs(historyQuery);

      // 현재 활성 설정인지 확인
      const currentSettings = await this.getBillingSettings(roomId);
      const isCurrentSettings = currentSettings?.id === targetSettings.id;

      const snapshot_result: PortvilleBillingSettingsSnapshot = {
        settingsId: targetSettings.id,
        roomId: targetSettings.roomId,
        roomNumber: targetSettings.roomNumber,
        effectiveDate,
        snapshotDate: targetSettings.effectiveFrom,
        settings: targetSettings,
        isCurrentSettings,
        nextChangeDate,
        historyCount: historySnapshot.size
      };

      logger.debug(`getBillingSettingsAtDate - 설정 스냅샷 조회 완료:`, {
        settingsId: targetSettings.id,
        version: targetSettings.version,
        isCurrentSettings,
        historyCount: historySnapshot.size
      });

      return snapshot_result;
    } catch (error) {
      logger.error('시점별 설정 조회 실패:', error);
      throw new BillingError('설정 조회에 실패했습니다.', 'GET_SETTINGS_AT_DATE_ERROR', error);
    }
  }

  /**
   * 호실의 설정 변경 히스토리 조회
   */
  async getBillingSettingsHistory(
    roomId: string,
    limitCount: number = 50
  ): Promise<PortvilleBillingSettingsHistory[]> {
    try {
      logger.debug(`getBillingSettingsHistory 호출 - roomId: ${roomId}, limit: ${limitCount}`);

      // 해당 호실의 모든 설정 ID 조회
      const settingsQuery = query(
        collection(db, 'portville_billing_settings'),
        where('roomId', '==', roomId)
      );
      const settingsSnapshot = await getDocs(settingsQuery);

      if (settingsSnapshot.empty) {
        return [];
      }

      const settingsIds = settingsSnapshot.docs.map(doc => doc.id);

      // 설정 ID들에 대한 히스토리 조회
      const historyQuery = query(
        collection(db, 'portville_billing_settings_history'),
        where('settingsId', 'in', settingsIds.slice(0, 10)), // Firestore 'in' 쿼리는 최대 10개
        orderBy('requestedAt', 'desc'),
        limit(limitCount)
      );

      const historySnapshot = await getDocs(historyQuery);

      const histories: PortvilleBillingSettingsHistory[] = historySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        requestedAt: doc.data().requestedAt.toDate(),
        processedAt: doc.data().processedAt?.toDate(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate(),
        previousSettings: this.convertTimestampsToDates(doc.data().previousSettings),
        currentSettings: this.convertTimestampsToDates(doc.data().currentSettings)
      } as PortvilleBillingSettingsHistory));

      logger.debug(`getBillingSettingsHistory - 히스토리 조회 완료: ${histories.length}개`);

      return histories;
    } catch (error) {
      logger.error('설정 히스토리 조회 실패:', error);
      throw new BillingError('설정 히스토리 조회에 실패했습니다.', 'GET_SETTINGS_HISTORY_ERROR', error);
    }
  }

  /**
   * 설정 버전별 타임라인 조회
   */
  async getBillingSettingsTimeline(roomId: string): Promise<{
    current: PortvilleBillingSettings | null;
    timeline: Array<{
      version: number;
      effectiveFrom: Date;
      effectiveTo?: Date;
      changeReason: string;
      changedFields: string[];
      isActive: boolean;
      createdBy: string;
    }>;
  }> {
    try {
      logger.debug(`getBillingSettingsTimeline 호출 - roomId: ${roomId}`);

      // 현재 활성 설정 조회
      const currentSettings = await this.getBillingSettings(roomId);

      // 해당 호실의 모든 설정 조회
      const allSettingsQuery = query(
        collection(db, 'portville_billing_settings'),
        where('roomId', '==', roomId),
        orderBy('effectiveFrom', 'desc'),
        orderBy('version', 'desc')
      );

      const allSettingsSnapshot = await getDocs(allSettingsQuery);

      const timeline = allSettingsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          version: data.version || 1,
          effectiveFrom: data.effectiveFrom.toDate(),
          effectiveTo: data.effectiveTo?.toDate(),
          changeReason: data.changeReason || '설정 업데이트',
          changedFields: [], // 히스토리에서 별도 조회 필요
          isActive: data.isActive || false,
          createdBy: data.createdBy
        };
      });

      logger.debug(`getBillingSettingsTimeline - 타임라인 조회 완료: ${timeline.length}개`);

      return {
        current: currentSettings,
        timeline
      };
    } catch (error) {
      logger.error('설정 타임라인 조회 실패:', error);
      throw new BillingError('설정 타임라인 조회에 실패했습니다.', 'GET_SETTINGS_TIMELINE_ERROR', error);
    }
  }

  /**
   * Timestamp를 Date로 변환하는 헬퍼 함수
   */
  private convertTimestampsToDates(obj: any): any {
    if (!obj) return obj;

    const converted = { ...obj };

    // Timestamp 필드들을 Date로 변환
    if (converted.effectiveFrom?.toDate) {
      converted.effectiveFrom = converted.effectiveFrom.toDate();
    }
    if (converted.effectiveTo?.toDate) {
      converted.effectiveTo = converted.effectiveTo.toDate();
    }
    if (converted.createdAt?.toDate) {
      converted.createdAt = converted.createdAt.toDate();
    }
    if (converted.updatedAt?.toDate) {
      converted.updatedAt = converted.updatedAt.toDate();
    }

    return converted;
  }

  // ==================== 사용량 기록 관리 ====================

  /**
   * 월별 사용량 기록 조회
   */
  async getUsageRecords(year: number, month: number): Promise<PortvilleUsageRecord[]> {
    try {
      const q = query(
        collection(db, 'portville_usage_records'),
        where('period.year', '==', year),
        where('period.month', '==', month),
        orderBy('roomNumber')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          recordedAt: data.recordedAt.toDate(),
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate()
        } as PortvilleUsageRecord;
      });
    } catch (error) {
      logger.error('사용량 기록 조회 실패:', error);
      throw new BillingError('사용량 기록 조회에 실패했습니다.', 'GET_USAGE_ERROR', error);
    }
  }

  /**
   * 특정 호실의 사용량 기록 조회
   */
  async getRoomUsageRecord(
    roomId: string,
    year: number,
    month: number
  ): Promise<PortvilleUsageRecord | null> {
    try {
      const q = query(
        collection(db, 'portville_usage_records'),
        where('roomId', '==', roomId),
        where('period.year', '==', year),
        where('period.month', '==', month),
        limit(QUERY_LIMITS.SINGLE_DOCUMENT)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      
      const data = snapshot.docs[0].data();
      return {
        id: snapshot.docs[0].id,
        ...data,
        recordedAt: data.recordedAt.toDate(),
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate()
      } as PortvilleUsageRecord;
    } catch (error) {
      logger.error('호실 사용량 기록 조회 실패:', error);
      throw new BillingError('사용량 기록 조회에 실패했습니다.', 'GET_ROOM_USAGE_ERROR', error);
    }
  }

  /**
   * 사용량 기록 저장
   */
  async saveUsageRecords(
    records: UsageRecordFormData[],
    recordedBy: string
  ): Promise<string[]> {
    try {
      const batch = writeBatch(db);
      const now = Timestamp.now();
      const docIds: string[] = [];

      for (const record of records) {
        // 기존 기록이 있는지 확인
        const existing = await this.getRoomUsageRecord(
          record.roomId,
          record.year,
          record.month
        );

        const usageData = {
          roomId: record.roomId,
          roomNumber: record.roomNumber,
          period: {
            year: record.year,
            month: record.month
          },
          electricityIndividualUsage: record.electricityIndividualUsage || null,
          electricityCommonUsage: record.electricityCommonUsage || null,
          waterIndividualUsage: record.waterIndividualUsage || null,
          waterCommonUsage: record.waterCommonUsage || null,
          recordedAt: now,
          recordedBy,
          updatedAt: now
        };

        if (existing) {
          // 기존 기록 업데이트
          const docRef = doc(db, 'portville_usage_records', existing.id);
          batch.update(docRef, usageData);
          docIds.push(existing.id);
        } else {
          // 새 기록 생성
          const docRef = doc(collection(db, 'portville_usage_records'));
          batch.set(docRef, {
            ...usageData,
            createdAt: now
          });
          docIds.push(docRef.id);
        }
      }

      await batch.commit();
      return docIds;
    } catch (error) {
      logger.error('사용량 기록 저장 실패:', error);
      throw new BillingError('사용량 기록 저장에 실패했습니다.', 'SAVE_USAGE_ERROR', error);
    }
  }

  /**
   * 사용량 기록 삭제
   */
  async deleteUsageRecord(recordId: string): Promise<void> {
    try {
      const docRef = doc(db, 'portville_usage_records', recordId);
      await deleteDoc(docRef);
    } catch (error) {
      logger.error('사용량 기록 삭제 실패:', error);
      throw new BillingError('사용량 기록 삭제에 실패했습니다.', 'DELETE_USAGE_ERROR', error);
    }
  }

  // ==================== 청구서 관리 ====================

  /**
   * 관리비 계산
   */
  private calculateBillAmount(input: BillCalculationInput): PortvilleBill['billItems'] {
    const { settings, usageRecord, buildingUsageRecord, roomBillingCalculation } = input;
    const { baseRates, roomSettings } = settings;

    const billItems = {
      monthlyRent: baseRates.monthlyRent,
      managementFee: baseRates.managementFee,
      managementFeeVAT: calculateVAT(baseRates.managementFee, baseRates.managementFeeVATRate / 100),
      electricityFee: 0,
      electricityFeeVAT: 0,
      electricityFund: 0,
      waterFee: 0,
      communicationFee: baseRates.communicationFee,
      communicationFeeVAT: calculateVAT(baseRates.communicationFee, baseRates.communicationFeeVATRate / 100),
      offsetProcessingFee: 0,
      offsetProcessingFeeVAT: 0,
      overdueAmount: 0,
      overduePenalty: 0,
      expectedPenalty: 0
    };

    // 호실별 요금 계산 결과가 있는 경우 사용
    if (roomBillingCalculation) {
      billItems.electricityFee = roomBillingCalculation.electricity.totalFee;
      billItems.electricityFund = roomBillingCalculation.electricity.fund;
      billItems.waterFee = roomBillingCalculation.water.totalFee;
      billItems.offsetProcessingFee = roomBillingCalculation.electricity.offsetFee;
      
      // 부가세 계산 (기본 전기요금 부가세율 사용)
      billItems.electricityFeeVAT = calculateVAT(roomBillingCalculation.electricity.totalFee, VAT_RATES.ELECTRICITY_FEE / 100);
      billItems.offsetProcessingFeeVAT = calculateVAT(roomBillingCalculation.electricity.offsetFee, VAT_RATES.ELECTRICITY_FEE / 100);
      
      logger.debug(`호실별 요금 계산 결과 사용:`, {
        roomId: roomBillingCalculation.roomId,
        roomNumber: roomBillingCalculation.roomNumber,
        electricityFee: billItems.electricityFee,
        electricityFund: billItems.electricityFund,
        waterFee: billItems.waterFee,
        offsetProcessingFee: billItems.offsetProcessingFee,
        electricityFeeVAT: billItems.electricityFeeVAT,
        offsetProcessingFeeVAT: billItems.offsetProcessingFeeVAT
      });
    }
    // 건물 전체 요금이 있는 경우 면적기여비율로 분할
    else if (buildingUsageRecord) {
      const electricityAreaRatio = roomSettings.electricityAreaContributionRatio !== undefined ? roomSettings.electricityAreaContributionRatio : ((roomSettings as any).areaContributionRatio !== undefined ? (roomSettings as any).areaContributionRatio : AREA_CONTRIBUTION_RATIOS.ELECTRICITY_DEFAULT);
      const waterAreaRatio = roomSettings.waterAreaContributionRatio !== undefined ? roomSettings.waterAreaContributionRatio : ((roomSettings as any).areaContributionRatio !== undefined ? (roomSettings as any).areaContributionRatio : AREA_CONTRIBUTION_RATIOS.WATER_DEFAULT);
      
      // 전기요금 계산 (면적기여비율 기반)
      const totalElectricityFee = buildingUsageRecord.totalElectricityFee;
      const totalBaseFee = buildingUsageRecord.totalBaseFee;
      const totalOffsetFee = buildingUsageRecord.totalOffsetFee;
      const totalElectricityFund = buildingUsageRecord.totalElectricityFund;
      
      billItems.electricityFee = Math.round(totalElectricityFee * electricityAreaRatio);
      billItems.electricityFund = Math.round(totalElectricityFund * electricityAreaRatio);
      billItems.offsetProcessingFee = Math.round(totalOffsetFee * electricityAreaRatio);
      
      // 전기요금 부가세
      billItems.electricityFeeVAT = calculateVAT(billItems.electricityFee, VAT_RATES.STANDARD_VAT);
      
      // 수도요금 계산 (수도 면적기여비율 기반) - 올림 처리
      const totalWaterFee = buildingUsageRecord.totalWaterFee;
      const waterFeeRaw = totalWaterFee * waterAreaRatio;
      
      // logger.debug(`청구서 수도료 계산 (올림 전):`, {
      //   totalWaterFee: totalWaterFee,
      //   waterAreaRatio: waterAreaRatio,
      //   waterFeeRaw: waterFeeRaw
      // });

      billItems.waterFee = roundWaterFee(waterFeeRaw);

      // logger.debug(`청구서 수도료 계산 (올림 후):`, {
      //   waterFee: billItems.waterFee
      // });
    }

    return billItems;
  }

  /**
   * 전월 연체 청구서 조회
   */
  private async getPreviousMonthOverdueBill(
    roomId: string,
    year: number,
    month: number
  ): Promise<PortvilleBill | null> {
    try {
      // 전월 계산
      let prevYear = year;
      let prevMonth = month - 1;
      if (prevMonth === 0) {
        prevMonth = 12;
        prevYear = year - 1;
      }

      const q = query(
        collection(db, 'portville_bills'),
        where('roomId', '==', roomId),
        where('billingPeriod.year', '==', prevYear),
        where('billingPeriod.month', '==', prevMonth),
        where('status', '==', 'overdue'),
        limit(QUERY_LIMITS.SINGLE_DOCUMENT)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;

      const data = snapshot.docs[0].data();
      return {
        id: snapshot.docs[0].id,
        ...data,
        billingPeriod: {
          ...data.billingPeriod,
          startDate: data.billingPeriod.startDate.toDate(),
          endDate: data.billingPeriod.endDate.toDate()
        },
        issuedDate: data.issuedDate.toDate(),
        dueDate: data.dueDate.toDate(),
        paidDate: data.paidDate?.toDate(),
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate()
      } as PortvilleBill;
    } catch (error) {
      logger.error('전월 연체 청구서 조회 실패:', error);
      return null;
    }
  }

  /**
   * 청구서 생성
   */
  async generateBills(request: BillGenerationRequest): Promise<string[]> {
    try {
      const now = Timestamp.now();
      const billIds: string[] = [];

      for (const roomId of request.roomIds) {
        try {
          // 호실 정보 조회
          const roomDoc = await getDoc(doc(db, 'portville_rooms', roomId));
          if (!roomDoc.exists()) {
            throw new BillGenerationError(`호실을 찾을 수 없습니다.`, roomId);
          }

          const roomData = roomDoc.data();
          
          // 입주 기록 조회 (호실인 경우에만)
          let residencyData: any = null;
          if (roomData.roomType === '호실') {
            if (!roomData.currentResidencyId) {
              logger.warn(`호실 ${roomData.roomNumber}에 현재 입주 기록이 없습니다.`);
              continue;
            }

            const residencyDoc = await getDoc(doc(db, 'portville_residencies', roomData.currentResidencyId));
            if (!residencyDoc.exists()) {
              logger.warn(`호실 ${roomData.roomNumber}의 입주 기록을 찾을 수 없습니다.`);
              continue;
            }

            residencyData = residencyDoc.data();
          }
          
          // 관리비 설정 조회
          const settings = await this.getBillingSettings(roomId);
          if (!settings) {
            throw new BillGenerationError(`호실 ${roomData.roomNumber} (ID: ${roomId})의 관리비 설정이 없습니다.`, roomId);
          }

          // 사용량 기록 조회
          const usageRecord = await this.getRoomUsageRecord(
            roomId,
            request.billingPeriod.year,
            request.billingPeriod.month
          );

          // 호실별 요금 계산 결과 조회
          const billingCalculations = await this.getBillingCalculations(
            request.billingPeriod.year,
            request.billingPeriod.month
          );
          const roomBillingCalculation = billingCalculations.find(calc => calc.roomId === roomId);

          // 기존 청구서 확인 및 덮어쓰기 처리
          const existingBillQuery = query(
            collection(db, 'portville_bills'),
            where('roomId', '==', roomId),
            where('billingPeriod.year', '==', request.billingPeriod.year),
            where('billingPeriod.month', '==', request.billingPeriod.month),
            limit(QUERY_LIMITS.SINGLE_DOCUMENT)
          );
          const existingBillSnapshot = await getDocs(existingBillQuery);
          
          let existingBillId: string | null = null;
          let existingCreatedAt: Timestamp | null = null;
          if (!existingBillSnapshot.empty) {
            const existingBill = existingBillSnapshot.docs[0];
            const existingBillData = existingBill.data();
            
            // 이미 지불된 청구서는 덮어쓰지 않음
            if (existingBillData.status === 'paid') {
              logger.warn(`호실 ${roomData.roomNumber}의 ${request.billingPeriod.year}년 ${request.billingPeriod.month}월 청구서가 이미 지불되었습니다. 덮어쓰기를 건너뜁니다.`);
              continue;
            }
            
            // 지불되지 않은 청구서는 덮어쓰기
            existingBillId = existingBill.id;
            existingCreatedAt = existingBillData.createdAt; // 기존 createdAt 보존
            logger.debug(`기존 청구서 덮어쓰기: 호실 ${roomData.roomNumber}, 청구서 ID: ${existingBillId}`);
          }

          // 관리비 계산
          const billItems = this.calculateBillAmount({
            settings,
            usageRecord: usageRecord || undefined,
            billingPeriod: request.billingPeriod,
            roomBillingCalculation: roomBillingCalculation ? {
              ...roomBillingCalculation,
              ratios: {
                electricityAreaContribution: (roomBillingCalculation.ratios as any).electricityAreaContribution || (roomBillingCalculation.ratios as any).areaContribution || 0,
                waterAreaContribution: (roomBillingCalculation.ratios as any).waterAreaContribution || (roomBillingCalculation.ratios as any).areaContribution || 0,
                electricityIndividual: roomBillingCalculation.ratios.electricityIndividual,
                waterIndividual: roomBillingCalculation.ratios.waterIndividual
              }
            } : undefined
          });

          // 전월 연체 청구서 확인 및 미납금 계산
          const previousOverdueBill = await this.getPreviousMonthOverdueBill(
            roomId,
            request.billingPeriod.year,
            request.billingPeriod.month
          );

          // 미납금과 연체 가산금을 분리하여 계산
          const overdueAmount = previousOverdueBill ? previousOverdueBill.totalAmount : 0;
          const overduePenalty = previousOverdueBill ? (previousOverdueBill.billItems.expectedPenalty || 0) : 0;

          // billItems에 미납금 및 연체 가산금 추가
          billItems.overdueAmount = overdueAmount;
          billItems.overduePenalty = overduePenalty;
          billItems.expectedPenalty = 0; // 현재 청구서의 예상 가산금은 0으로 초기화

          const totalAmount = Object.values(billItems).reduce((sum, amount) => sum + amount, 0);

          // 청구 기간 계산
          const startDate = new Date(request.billingPeriod.year, request.billingPeriod.month - 1, 1);
          const endDate = new Date(request.billingPeriod.year, request.billingPeriod.month, 0);

          // 납부 기한 계산: 청구월의 다음달 말일
          const dueDate = new Date(request.billingPeriod.year, request.billingPeriod.month + 1, 0); // 다음달 말일

          logger.debug(`청구서 생성 중:`, {
            roomId,
            roomNumber: roomData.roomNumber,
            year: request.billingPeriod.year,
            month: request.billingPeriod.month,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            dueDate: dueDate.toISOString(),
            totalAmount,
            residentName: residencyData?.residentInfo?.name || '시설물'
          });

          // 사용량 정보 포함 (RoomBillingCalculation의 usage 데이터 사용)
          const usageInfo = roomBillingCalculation ? {
            electricityIndividualUsage: roomBillingCalculation.usage.electricityIndividual,
            electricityCommonUsage: roomBillingCalculation.usage.electricityCommon,
            waterIndividualUsage: roomBillingCalculation.usage.waterIndividual,
            waterCommonUsage: roomBillingCalculation.usage.waterCommon
          } : undefined;

          const billData: Omit<PortvilleBill, 'id'> = {
            roomId,
            roomNumber: roomData.roomNumber,
            roomType: roomData.roomType || '호실',
            residentUid: residencyData?.residentUid,
            residentInfo: roomData.roomType === '호실' ? residencyData?.residentInfo : undefined,
            facilityInfo: roomData.roomType === '시설물' ? roomData.facilityInfo : undefined,
            billingPeriod: {
              year: request.billingPeriod.year,
              month: request.billingPeriod.month,
              startDate,
              endDate
            },
            billItems,
            usageInfo,
            totalAmount,
            status: 'pending',
            issuedDate: request.issuedDate,
            dueDate: dueDate,
            createdAt: now.toDate(),
            updatedAt: now.toDate()
          };

          // 결정적 DocID 생성: bill:${roomId}:${YYYY-MM}
          const deterministicDocId = `bill:${roomId}:${request.billingPeriod.year}-${String(request.billingPeriod.month).padStart(2, '0')}`;

          const firestoreData = {
            ...billData,
            billingPeriod: {
              ...billData.billingPeriod,
              startDate: Timestamp.fromDate(startDate),
              endDate: Timestamp.fromDate(endDate)
            },
            issuedDate: Timestamp.fromDate(request.issuedDate),
            dueDate: Timestamp.fromDate(dueDate),
            createdAt: existingCreatedAt || now, // 기존 createdAt이 있으면 사용, 없으면 새로 생성
            updatedAt: now
          };

          logger.debug(`Firestore에 저장할 데이터:`, {
            docId: deterministicDocId,
            roomNumber: firestoreData.roomNumber,
            year: firestoreData.billingPeriod.year,
            month: firestoreData.billingPeriod.month,
            startDate: firestoreData.billingPeriod.startDate.toDate().toISOString(),
            endDate: firestoreData.billingPeriod.endDate.toDate().toISOString(),
            status: firestoreData.status,
            totalAmount: firestoreData.totalAmount,
            isUpdate: !!existingBillId,
            createdAt: firestoreData.createdAt.toDate().toISOString(),
            updatedAt: firestoreData.updatedAt.toDate().toISOString()
          });

          // 트랜잭션 기반으로 청구서 생성/업데이트
          const billRef = doc(db, 'portville_bills', deterministicDocId);

          await runTransaction(db, async (transaction) => {
            const existingBill = await transaction.get(billRef);

            if (existingBill.exists()) {
              const existingBillData = existingBill.data();

              // 이미 지불된 청구서는 덮어쓰지 않음
              if (existingBillData.status === 'paid') {
                logger.warn(`호실 ${roomData.roomNumber}의 ${request.billingPeriod.year}년 ${request.billingPeriod.month}월 청구서가 이미 지불되었습니다. 덮어쓰기를 건너뜁니다.`);
                return; // 트랜잭션 내에서 continue 대신 return 사용
              }

              // 지불되지 않은 청구서는 덮어쓰기
              transaction.set(billRef, firestoreData, { merge: true });
              logger.debug(`기존 청구서 덮어쓰기: 호실 ${roomData.roomNumber}, 청구서 ID: ${deterministicDocId}`);
            } else {
              // 존재하지 않으면 새로 생성
              transaction.set(billRef, firestoreData);
              logger.debug(`새 청구서 생성: 호실 ${roomData.roomNumber}, 청구서 ID: ${deterministicDocId}`);
            }
          });

          billIds.push(deterministicDocId);
        } catch (error) {
          logger.error(`호실 ${roomId} 청구서 생성 실패:`, error);
          if (error instanceof BillGenerationError) {
            throw error;
          }
          throw new BillGenerationError(`호실 청구서 생성에 실패했습니다.`, roomId, error);
        }
      }

      return billIds;
    } catch (error) {
      logger.error('청구서 생성 실패:', error);
      if (error instanceof BillGenerationError) {
        throw error;
      }
      throw new BillingError('청구서 생성에 실패했습니다.', 'GENERATE_BILLS_ERROR', error);
    }
  }

  /**
   * 월별 청구서 목록 조회 (최적화된 서버사이드 필터링)
   */
  async getBillsByPeriod(year: number, month: number): Promise<PortvilleBill[]> {
    try {
      logger.startTimer(`getBillsByPeriod-${year}-${month}`);
      logger.debug(`청구서 조회 시작: ${year}년 ${month}월`);
      
      // 🚀 성능 최적화: 서버사이드 필터링 사용
      const q = query(
        collection(db, 'portville_bills'),
        where('billingPeriod.year', '==', year),
        where('billingPeriod.month', '==', month),
        orderBy('roomNumber') // 서버사이드 정렬
      );
      
      const snapshot = await getDocs(q);
      logger.debug(`청구서 조회 완료: ${snapshot.docs.length}개 문서`);
      
      // 🚀 성능 최적화: 공통 변환 함수 사용
      const bills = snapshot.docs.map(transformBillDocument);
      
      logger.endTimer(`getBillsByPeriod-${year}-${month}`);
      return bills;
    } catch (error) {
      logger.error('청구서 목록 조회 실패:', error);
      throw new BillingError('청구서 목록 조회에 실패했습니다.', 'GET_BILLS_ERROR', error);
    }
  }

  /**
   * 특정 호실의 청구서 목록 조회 (최적화된 서버사이드 필터링)
   */
  async getRoomBills(roomId: string, limitCount?: number): Promise<PortvilleBill[]> {
    try {
      logger.startTimer(`getRoomBills-${roomId}`);
      logger.debug(`호실 청구서 조회 시작: roomId=${roomId}, limit=${limitCount}`);
      
      // 🚀 성능 최적화: 서버사이드 필터링 사용
      let q = query(
        collection(db, 'portville_bills'),
        where('roomId', '==', roomId),
        orderBy('billingPeriod.year', 'desc'),
        orderBy('billingPeriod.month', 'desc')
      );
      
      // 제한이 있으면 서버사이드에서 적용
      if (limitCount) {
        q = query(q, limit(limitCount));
      }
      
      const snapshot = await getDocs(q);
      logger.debug(`호실 청구서 조회 완료: ${snapshot.docs.length}개 문서`);
      
      // 🚀 성능 최적화: 공통 변환 함수 사용
      const bills = snapshot.docs.map(transformBillDocument);
      
      logger.endTimer(`getRoomBills-${roomId}`);
      return bills;
    } catch (error) {
      logger.error('호실 청구서 조회 실패:', error);
      throw new BillingError('청구서 조회에 실패했습니다.', 'GET_ROOM_BILLS_ERROR', error);
    }
  }

  /**
   * 청구서 가산금 업데이트
   */
  async updateBillPenalty(billId: string, penaltyAmount: number): Promise<void> {
    try {
      const billRef = doc(db, 'portville_bills', billId);
      const billDoc = await getDoc(billRef);
      
      if (!billDoc.exists()) {
        throw new BillingError('청구서를 찾을 수 없습니다.', 'BILL_NOT_FOUND');
      }

      // 연체된 청구서의 예상 가산금 필드에 저장 (총액은 변경하지 않음)
      const updates = {
        'billItems.expectedPenalty': penaltyAmount,
        updatedAt: Timestamp.now()
      };

      await updateDoc(billRef, updates);
      
      logger.debug(`예상 가산금 업데이트 완료:`, {
        billId,
        penaltyAmount,
        note: '다음달에 청구할 예상 가산금으로 저장됨'
      });
    } catch (error) {
      logger.error('예상 가산금 업데이트 실패:', error);
      if (error instanceof BillingError) {
        throw error;
      }
      throw new BillingError('예상 가산금 업데이트에 실패했습니다.', 'UPDATE_PENALTY_ERROR', error);
    }
  }


  /**
   * 청구서 상태 업데이트
   */
  async updateBillStatus(
    billId: string,
    status: PortvilleBill['status'],
    paidDate?: Date
  ): Promise<void> {
    try {
      const updateData: any = {
        status,
        updatedAt: Timestamp.now()
      };

      if (status === 'paid' && paidDate) {
        updateData.paidDate = Timestamp.fromDate(paidDate);
      }

      await updateDoc(doc(db, 'portville_bills', billId), updateData);
    } catch (error) {
      logger.error('청구서 상태 업데이트 실패:', error);
      throw new BillingError('청구서 상태 업데이트에 실패했습니다.', 'UPDATE_BILL_STATUS_ERROR', error);
    }
  }

  /**
   * 청구서 삭제
   */
  async deleteBill(billId: string): Promise<void> {
    try {
      // 청구서 존재 확인
      const billRef = doc(db, 'portville_bills', billId);
      const billSnapshot = await getDoc(billRef);
      
      if (!billSnapshot.exists()) {
        throw new BillingError('삭제하려는 청구서가 존재하지 않습니다.', 'BILL_NOT_FOUND');
      }

      const billData = billSnapshot.data();
      
      // 이미 납부된 청구서는 삭제 불가
      if (billData.status === 'paid') {
        throw new BillingError('이미 납부된 청구서는 삭제할 수 없습니다.', 'CANNOT_DELETE_PAID_BILL');
      }

      // 청구서 삭제 실행
      await deleteDoc(billRef);
      
      logger.info(`청구서 삭제 완료: ${billId} (호실: ${billData.roomNumber})`);
    } catch (error) {
      if (error instanceof BillingError) {
        throw error;
      }
      logger.error('청구서 삭제 실패:', error);
      throw new BillingError('청구서 삭제에 실패했습니다.', 'DELETE_BILL_ERROR', error);
    }
  }

  // ==================== SMS 인증 관리 ====================

  /**
   * SMS 인증 코드 발송
   */
  async sendSmsAuthCode(request: SmsAuthRequest): Promise<string> {
    try {
      // 호실번호와 전화번호로 입주자 확인
      const roomDoc = await getDoc(doc(db, 'portville_rooms', request.roomNumber));
      if (!roomDoc.exists()) {
        throw new SmsAuthError('존재하지 않는 호실번호입니다.');
      }

      const roomData = roomDoc.data();
      if (!roomData.currentResidencyId) {
        throw new SmsAuthError('해당 호실에 입주 중인 주민이 없습니다.');
      }

      const residencyDoc = await getDoc(doc(db, 'portville_residencies', roomData.currentResidencyId));
      if (!residencyDoc.exists()) {
        throw new SmsAuthError('입주자 정보를 찾을 수 없습니다.');
      }

      const residencyData = residencyDoc.data();
      if (residencyData.residentInfo.phoneNumber !== request.phoneNumber) {
        throw new SmsAuthError('등록된 전화번호와 일치하지 않습니다.');
      }

      // 인증코드 생성
      const verificationCode = generateSMSCode();
      
      // SMS 발송 (실제 SMS 서비스 연동 필요)
      // await this.sendSms(request.phoneNumber, `포트빌 관리비 조회 인증코드: ${verificationCode}`);

      // 인증 토큰 저장
      const tokenData = {
        roomNumber: request.roomNumber,
        phoneNumber: request.phoneNumber,
        verificationCode,
        expiresAt: Timestamp.fromDate(getSMSExpiryTime()), // SMS 인증 만료 시간
        isUsed: false,
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'portville_billing_auth_tokens'), tokenData);
      
      // 개발 환경에서는 콘솔에 인증코드 출력
      if (process.env.NODE_ENV === 'development') {
        logger.info(`SMS 인증코드 (${request.phoneNumber}): ${verificationCode}`);
      }

      return docRef.id;
    } catch (error) {
      logger.error('SMS 인증코드 발송 실패:', error);
      if (error instanceof SmsAuthError) {
        throw error;
      }
      throw new SmsAuthError('SMS 인증코드 발송에 실패했습니다.');
    }
  }

  /**
   * SMS 인증 코드 확인
   */
  async verifySmsAuthCode(verification: SmsAuthVerification): Promise<string> {
    try {
      const q = query(
        collection(db, 'portville_billing_auth_tokens'),
        where('roomNumber', '==', verification.roomNumber),
        where('phoneNumber', '==', verification.phoneNumber),
        where('verificationCode', '==', verification.verificationCode),
        where('isUsed', '==', false),
        where('expiresAt', '>', Timestamp.now()),
        orderBy('createdAt', 'desc'),
        limit(QUERY_LIMITS.SINGLE_DOCUMENT)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        throw new SmsAuthError('유효하지 않은 인증코드입니다.');
      }

      const tokenDoc = snapshot.docs[0];
      
      // 토큰 사용 처리
      await updateDoc(tokenDoc.ref, {
        isUsed: true,
        updatedAt: Timestamp.now()
      });

      // 임시 세션 토큰 생성 (30분 유효)
      const sessionToken = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // 세션 토큰을 별도 컬렉션에 저장하거나 JWT 토큰 사용 가능
      // 여기서는 간단히 토큰 ID 반환
      return sessionToken;
    } catch (error) {
      logger.error('SMS 인증 확인 실패:', error);
      if (error instanceof SmsAuthError) {
        throw error;
      }
      throw new SmsAuthError('SMS 인증 확인에 실패했습니다.');
    }
  }

  // ==================== 입주자 조회 기능 ====================

  /**
   * UID 기반 관리비 조회
   */
  async getBillingDataByUid(uid: string): Promise<BillingViewData> {
    try {
      // 입주 기록 조회
      const residencyQuery = query(
        collection(db, 'portville_residencies'),
        where('residentUid', '==', uid),
        where('status', '==', 'active'),
        limit(QUERY_LIMITS.SINGLE_DOCUMENT)
      );

      const residencySnapshot = await getDocs(residencyQuery);
      if (residencySnapshot.empty) {
        throw new BillingError('입주 정보를 찾을 수 없습니다.', 'NO_RESIDENCY_FOUND');
      }

      const residencyData = residencySnapshot.docs[0].data();
      const roomId = residencyData.roomId;

      return await this.getBillingDataByRoomId(roomId);
    } catch (error) {
      logger.error('UID 기반 관리비 조회 실패:', error);
      if (error instanceof BillingError) {
        throw error;
      }
      throw new BillingError('관리비 조회에 실패했습니다.', 'GET_BILLING_BY_UID_ERROR', error);
    }
  }

  /**
   * 호실번호 기반 관리비 조회 (SMS 인증 후)
   */
  async getBillingDataByRoomNumber(roomNumber: string): Promise<BillingViewData> {
    try {
      const roomQuery = query(
        collection(db, 'portville_rooms'),
        where('roomNumber', '==', roomNumber),
        limit(QUERY_LIMITS.SINGLE_DOCUMENT)
      );

      const roomSnapshot = await getDocs(roomQuery);
      if (roomSnapshot.empty) {
        throw new BillingError('호실을 찾을 수 없습니다.', 'ROOM_NOT_FOUND');
      }

      const roomId = roomSnapshot.docs[0].id;
      return await this.getBillingDataByRoomId(roomId);
    } catch (error) {
      logger.error('호실번호 기반 관리비 조회 실패:', error);
      if (error instanceof BillingError) {
        throw error;
      }
      throw new BillingError('관리비 조회에 실패했습니다.', 'GET_BILLING_BY_ROOM_NUMBER_ERROR', error);
    }
  }

  /**
   * 호실 ID 기반 관리비 조회 (공통)
   */
  private async getBillingDataByRoomId(roomId: string): Promise<BillingViewData> {
    try {
      // 호실 정보 조회
      const roomDoc = await getDoc(doc(db, 'portville_rooms', roomId));
      if (!roomDoc.exists()) {
        throw new BillingError('호실을 찾을 수 없습니다.', 'ROOM_NOT_FOUND');
      }

      const roomData = roomDoc.data();
      
      // 입주 기록 조회
      if (!roomData.currentResidencyId) {
        throw new BillingError('입주 정보가 없습니다.', 'NO_RESIDENCY');
      }

      const residencyDoc = await getDoc(doc(db, 'portville_residencies', roomData.currentResidencyId));
      if (!residencyDoc.exists()) {
        throw new BillingError('입주 정보를 찾을 수 없습니다.', 'RESIDENCY_NOT_FOUND');
      }

      const residencyData = residencyDoc.data();

      // 청구서 목록 조회
      const bills = await this.getRoomBills(roomId);
      
      // 당월 청구서 찾기
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      
      const currentBill = bills.find(bill => 
        bill.billingPeriod.year === currentYear && 
        bill.billingPeriod.month === currentMonth
      );

      // 미납 총액 계산
      const unpaidBills = bills.filter(bill => bill.status === 'pending' || bill.status === 'overdue');
      const totalUnpaidAmount = unpaidBills.reduce((sum, bill) => sum + bill.totalAmount, 0);

      return {
        roomInfo: {
          roomNumber: roomData.roomNumber,
          residentInfo: residencyData.residentInfo
        },
        currentBill,
        previousBills: bills.filter(bill => bill !== currentBill),
        totalUnpaidAmount
      };
    } catch (error) {
      logger.error('호실 ID 기반 관리비 조회 실패:', error);
      if (error instanceof BillingError) {
        throw error;
      }
      throw new BillingError('관리비 조회에 실패했습니다.', 'GET_BILLING_BY_ROOM_ID_ERROR', error);
    }
  }

  // ==================== 통계 및 리포트 ====================

  /**
   * 월별 관리비 통계 조회
   */
  async getBillingStatistics(year: number, month: number): Promise<BillingStatistics> {
    try {
      const bills = await this.getBillsByPeriod(year, month);
      
      const totalRooms = bills.length;
      const paidRooms = bills.filter(bill => bill.status === 'paid').length;
      const overdueRooms = bills.filter(bill => bill.status === 'overdue').length;
      
      const totalBilledAmount = bills.reduce((sum, bill) => sum + bill.totalAmount, 0);
      const totalPaidAmount = bills
        .filter(bill => bill.status === 'paid')
        .reduce((sum, bill) => sum + bill.totalAmount, 0);
      const totalOverdueAmount = bills
        .filter(bill => bill.status === 'overdue')
        .reduce((sum, bill) => sum + bill.totalAmount, 0);
      
      const collectionRate = calculatePercentage(totalPaidAmount, totalBilledAmount);

      return {
        totalRooms,
        billedRooms: totalRooms,
        paidRooms,
        overdueRooms,
        totalBilledAmount,
        totalPaidAmount,
        totalOverdueAmount,
        collectionRate
      };
    } catch (error) {
      logger.error('관리비 통계 조회 실패:', error);
      throw new BillingError('통계 조회에 실패했습니다.', 'GET_STATISTICS_ERROR', error);
    }
  }

  // ==================== 건물 전체 사용량 관리 ====================

  /**
   * 건물 전체 사용량 기록 저장
   */
  async saveBuildingUsageRecord(formData: BuildingUsageRecordFormData, createdBy: string): Promise<string> {
    try {
      const now = Timestamp.now();
      
      const usageData = {
        period: {
          year: formData.year,
          month: formData.month
        },
        totalElectricityFee: formData.totalElectricityFee,
        buildingIndividualUsage: formData.buildingIndividualUsage,
        buildingCommonUsage: formData.buildingCommonUsage,
        totalBaseFee: formData.totalBaseFee,
        totalOffsetFee: formData.totalOffsetFee,
        totalElectricityFund: formData.totalElectricityFund,
        // 이전 호환성을 위한 기존 필드 (포트빌 데이터로 설정)
        totalWaterFee: formData.totalWaterFee || formData.portvilleTotalWaterFee,
        buildingWaterIndividualUsage: formData.buildingWaterIndividualUsage || formData.portvilleBuildingWaterIndividualUsage,
        buildingWaterCommonUsage: formData.buildingWaterCommonUsage || formData.portvilleBuildingWaterCommonUsage,
        // 새로운 포트빌/늘해랑 분리 필드
        portvilleTotalWaterFee: formData.portvilleTotalWaterFee,
        portvilleBuildingWaterIndividualUsage: formData.portvilleBuildingWaterIndividualUsage,
        portvilleBuildingWaterCommonUsage: formData.portvilleBuildingWaterCommonUsage,
        neulhaerangTotalWaterFee: formData.neulhaerangTotalWaterFee,
        neulhaerangBuildingWaterIndividualUsage: formData.neulhaerangBuildingWaterIndividualUsage,
        neulhaerangBuildingWaterCommonUsage: formData.neulhaerangBuildingWaterCommonUsage,
        recordedAt: now.toDate(),
        recordedBy: createdBy,
        createdAt: now.toDate(),
        updatedAt: now.toDate()
      };

      const docRef = await addDoc(collection(db, 'portville_building_usage_records'), usageData);
      return docRef.id;
    } catch (error) {
      logger.error('건물 전체 사용량 저장 실패:', error);
      throw new BillingError('건물 전체 사용량 저장에 실패했습니다.', 'SAVE_BUILDING_USAGE_ERROR', error);
    }
  }

  /**
   * 건물 전체 사용량 기록 수정
   */
  async updateBuildingUsageRecord(recordId: string, formData: BuildingUsageRecordFormData, updatedBy: string): Promise<void> {
    try {
      const now = Timestamp.now();
      
      const usageData = {
        period: {
          year: formData.year,
          month: formData.month
        },
        totalElectricityFee: formData.totalElectricityFee,
        buildingIndividualUsage: formData.buildingIndividualUsage,
        buildingCommonUsage: formData.buildingCommonUsage,
        totalBaseFee: formData.totalBaseFee,
        totalOffsetFee: formData.totalOffsetFee,
        totalElectricityFund: formData.totalElectricityFund,
        // 이전 호환성을 위한 기존 필드 (포트빌 데이터로 설정)
        totalWaterFee: formData.totalWaterFee || formData.portvilleTotalWaterFee,
        buildingWaterIndividualUsage: formData.buildingWaterIndividualUsage || formData.portvilleBuildingWaterIndividualUsage,
        buildingWaterCommonUsage: formData.buildingWaterCommonUsage || formData.portvilleBuildingWaterCommonUsage,
        // 새로운 포트빌/늘해랑 분리 필드
        portvilleTotalWaterFee: formData.portvilleTotalWaterFee,
        portvilleBuildingWaterIndividualUsage: formData.portvilleBuildingWaterIndividualUsage,
        portvilleBuildingWaterCommonUsage: formData.portvilleBuildingWaterCommonUsage,
        neulhaerangTotalWaterFee: formData.neulhaerangTotalWaterFee,
        neulhaerangBuildingWaterIndividualUsage: formData.neulhaerangBuildingWaterIndividualUsage,
        neulhaerangBuildingWaterCommonUsage: formData.neulhaerangBuildingWaterCommonUsage,
        recordedBy: updatedBy,
        updatedAt: now.toDate()
      };

      await updateDoc(doc(db, 'portville_building_usage_records', recordId), usageData);
    } catch (error) {
      logger.error('건물 전체 사용량 수정 실패:', error);
      throw new BillingError('건물 전체 사용량 수정에 실패했습니다.', 'UPDATE_BUILDING_USAGE_ERROR', error);
    }
  }

  /**
   * 건물 전체 사용량 기록 삭제
   */
  async deleteBuildingUsageRecord(recordId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'portville_building_usage_records', recordId));
    } catch (error) {
      logger.error('건물 전체 사용량 삭제 실패:', error);
      throw new BillingError('건물 전체 사용량 삭제에 실패했습니다.', 'DELETE_BUILDING_USAGE_ERROR', error);
    }
  }

  /**
   * 건물 전체 사용량 기록 조회
   */
  async getBuildingUsageRecords(year?: number, month?: number): Promise<PortvilleBuildingUsageRecord[]> {
    try {
      let q = query(collection(db, 'portville_building_usage_records'), orderBy('period.year', 'desc'), orderBy('period.month', 'desc'));
      
      if (year !== undefined) {
        q = query(q, where('period.year', '==', year));
      }
      if (month !== undefined) {
        q = query(q, where('period.month', '==', month));
      }

      const querySnapshot = await getDocs(q);
      const records: PortvilleBuildingUsageRecord[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        records.push({
          id: doc.id,
          period: data.period,
          totalElectricityFee: data.totalElectricityFee,
          buildingIndividualUsage: data.buildingIndividualUsage,
          buildingCommonUsage: data.buildingCommonUsage,
          totalBaseFee: data.totalBaseFee,
          totalOffsetFee: data.totalOffsetFee,
          totalElectricityFund: data.totalElectricityFund,
          totalWaterFee: data.totalWaterFee,
          buildingWaterIndividualUsage: data.buildingWaterIndividualUsage,
          buildingWaterCommonUsage: data.buildingWaterCommonUsage,
          // 새로 추가된 포트빌/늘해랑 분리 필드들
          portvilleTotalWaterFee: data.portvilleTotalWaterFee,
          portvilleBuildingWaterIndividualUsage: data.portvilleBuildingWaterIndividualUsage,
          portvilleBuildingWaterCommonUsage: data.portvilleBuildingWaterCommonUsage,
          neulhaerangTotalWaterFee: data.neulhaerangTotalWaterFee,
          neulhaerangBuildingWaterIndividualUsage: data.neulhaerangBuildingWaterIndividualUsage,
          neulhaerangBuildingWaterCommonUsage: data.neulhaerangBuildingWaterCommonUsage,
          recordedAt: data.recordedAt.toDate(),
          recordedBy: data.recordedBy,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate()
        } as any);
      });

      return records;
    } catch (error) {
      logger.error('건물 전체 사용량 조회 실패:', error);
      throw new BillingError('건물 전체 사용량 조회에 실패했습니다.', 'GET_BUILDING_USAGE_ERROR', error);
    }
  }

  /**
   * 특정 기간의 건물 전체 사용량 기록 조회
   */
  async getBuildingUsageRecordByPeriod(year: number, month: number): Promise<PortvilleBuildingUsageRecord | null> {
    try {
      const q = query(
        collection(db, 'portville_building_usage_records'),
        where('period.year', '==', year),
        where('period.month', '==', month)
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        period: data.period,
        totalElectricityFee: data.totalElectricityFee,
        buildingIndividualUsage: data.buildingIndividualUsage,
        buildingCommonUsage: data.buildingCommonUsage,
        totalBaseFee: data.totalBaseFee,
        totalOffsetFee: data.totalOffsetFee,
        totalElectricityFund: data.totalElectricityFund,
        totalWaterFee: data.totalWaterFee,
        buildingWaterIndividualUsage: data.buildingWaterIndividualUsage,
        buildingWaterCommonUsage: data.buildingWaterCommonUsage,
        // 새로 추가된 포트빌/늘해랑 분리 필드들
        portvilleTotalWaterFee: data.portvilleTotalWaterFee,
        portvilleBuildingWaterIndividualUsage: data.portvilleBuildingWaterIndividualUsage,
        portvilleBuildingWaterCommonUsage: data.portvilleBuildingWaterCommonUsage,
        neulhaerangTotalWaterFee: data.neulhaerangTotalWaterFee,
        neulhaerangBuildingWaterIndividualUsage: data.neulhaerangBuildingWaterIndividualUsage,
        neulhaerangBuildingWaterCommonUsage: data.neulhaerangBuildingWaterCommonUsage,
        recordedAt: data.recordedAt.toDate(),
        recordedBy: data.recordedBy,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate()
      } as any;
    } catch (error) {
      logger.error('건물 전체 사용량 조회 실패:', error);
      throw new BillingError('건물 전체 사용량 조회에 실패했습니다.', 'GET_BUILDING_USAGE_ERROR', error);
    }
  }

  /**
   * 호실별 전기 및 수도 요금 계산 (2단계 방식)
   */
  async calculateRoomBilling(
    year: number,
    month: number,
    roomUsageRecords: PortvilleUsageRecord[],
    buildingUsageRecord: PortvilleBuildingUsageRecord,
    billingSettings: PortvilleBillingSettings[]
  ): Promise<RoomBillingCalculation[]> {
    try {
      const results: RoomBillingCalculation[] = [];

      // 성능 최적화: billingSettings를 Map으로 변환 (O(1) 검색)
      const settingsMap = new Map(
        billingSettings.map(setting => [setting.roomId, setting])
      );

      // 건물 전체 사용량 합계 계산
      const totalElectricityUsage = buildingUsageRecord.buildingIndividualUsage + buildingUsageRecord.buildingCommonUsage;
      const totalWaterUsage = buildingUsageRecord.buildingWaterIndividualUsage + buildingUsageRecord.buildingWaterCommonUsage;

      // 포트빌/늘해랑 별도 수도 사용량 계산
      const portvilleTotalWaterUsage = (buildingUsageRecord.portvilleBuildingWaterIndividualUsage || 0) + (buildingUsageRecord.portvilleBuildingWaterCommonUsage || 0);
      const neulhaerangTotalWaterUsage = (buildingUsageRecord.neulhaerangBuildingWaterIndividualUsage || 0) + (buildingUsageRecord.neulhaerangBuildingWaterCommonUsage || 0);

      // 건물 전체 수도 사용량 정보 출력
      console.log('\n=== 건물 전체 수도 사용량 정보 ===');
      console.log('포트빌:', {
        총요금: `${(buildingUsageRecord.portvilleTotalWaterFee || 0).toLocaleString()}원`,
        개별사용량: `${(buildingUsageRecord.portvilleBuildingWaterIndividualUsage || 0)}톤`,
        공용사용량: `${(buildingUsageRecord.portvilleBuildingWaterCommonUsage || 0)}톤`,
        합계사용량: `${portvilleTotalWaterUsage}톤`
      });
      console.log('늘해랑:', {
        총요금: `${(buildingUsageRecord.neulhaerangTotalWaterFee || 0).toLocaleString()}원`,
        개별사용량: `${(buildingUsageRecord.neulhaerangBuildingWaterIndividualUsage || 0)}톤`,
        공용사용량: `${(buildingUsageRecord.neulhaerangBuildingWaterCommonUsage || 0)}톤`,
        합계사용량: `${neulhaerangTotalWaterUsage}톤`
      });
      console.log('전체:', {
        총요금: `${buildingUsageRecord.totalWaterFee.toLocaleString()}원`,
        개별사용량: `${buildingUsageRecord.buildingWaterIndividualUsage}톤`,
        공용사용량: `${buildingUsageRecord.buildingWaterCommonUsage}톤`,
        합계사용량: `${totalWaterUsage}톤`
      });
      console.log('=================================\n');

      // ========== 1단계: 시설물 전력기금 계산 ==========
      let totalFacilityElectricityFund = 0;

      logger.debug('1단계: 시설물 계산 시작');
      logger.debug('전체 사용량 기록 수:', roomUsageRecords.length);
      logger.debug('설정 수:', billingSettings.length);
      
      // 설정 구조 확인
      logger.debug('첫 번째 설정 구조:', billingSettings[0]);
      logger.debug('설정들의 roomType 확인:');
      billingSettings.forEach(setting => {
        logger.debug(`  - 호실 ${setting.roomNumber}: roomType=${setting.roomType}, roomSettings.roomType=${setting.roomSettings?.roomType}`);
      });

      for (const usageRecord of roomUsageRecords) {
        const settings = settingsMap.get(usageRecord.roomId);
        if (!settings) {
          logger.warn(`호실 ${usageRecord.roomNumber}의 청구 설정을 찾을 수 없습니다.`);
          continue;
        }

        // 백워드 호환성: roomType이 없는 기존 설정은 '호실'로 간주
        const roomType = settings.roomType || '호실';
        logger.debug(`호실 ${usageRecord.roomNumber}: roomType=${roomType} (원본: ${settings.roomType})`);

        if (roomType === '시설물') {
          logger.debug(`시설물 ${usageRecord.roomNumber} 계산 중...`);
          const calculation = this.calculateFacilityBilling(
            usageRecord, 
            settings, 
            buildingUsageRecord, 
            totalElectricityUsage, 
            totalWaterUsage
          );
          results.push(calculation);
          totalFacilityElectricityFund += calculation.electricity.fund;
          // logger.debug(`시설물 ${usageRecord.roomNumber} 전력기금: ${calculation.electricity.fund}`);
        }
      }

      // logger.debug(`1단계 완료: 시설물 전력기금 총합 = ${totalFacilityElectricityFund}`);
      // logger.debug(`1단계 결과 수: ${results.length}`);

      // ========== 2단계: 호실 계산 (조정된 전력기금 사용) ==========
      const adjustedElectricityFund = buildingUsageRecord.totalElectricityFund - totalFacilityElectricityFund;

      // logger.debug('2단계: 호실 계산 시작');
      // logger.debug(`건물 전체 전력기금: ${buildingUsageRecord.totalElectricityFund}`);
      // logger.debug(`시설물 전력기금 총합: ${totalFacilityElectricityFund}`);
      // logger.debug(`조정된 전력기금: ${adjustedElectricityFund}`);

      // 음수 방지 검증
      if (adjustedElectricityFund < 0) {
        logger.warn('조정된 전력기금이 음수입니다:', {
          buildingTotalFund: buildingUsageRecord.totalElectricityFund,
          facilityTotalFund: totalFacilityElectricityFund,
          adjustedFund: adjustedElectricityFund
        });
      }

      let roomCount = 0;
      for (const usageRecord of roomUsageRecords) {
        const settings = settingsMap.get(usageRecord.roomId);
        if (!settings) {
          logger.debug(`설정 없음: ${usageRecord.roomNumber}`);
          continue;
        }

        // 백워드 호환성: roomType이 없는 기존 설정은 '호실'로 간주
        const roomType = settings.roomType || '호실';
        logger.debug(`호실 ${usageRecord.roomNumber}: roomType=${roomType} (원본: ${settings.roomType})`);

        if (roomType === '호실') {
          logger.debug(`일반 호실 ${usageRecord.roomNumber} 계산 중...`);

          // 호실의 세부 타입 (포트빌/늘해랑) 확인
          const rawRoomSubType = settings.roomSettings?.roomType || '포트빌'; // 기본값은 포트빌
          const roomSubType: '포트빌' | '늘해랑' = rawRoomSubType === '늘해랑' ? '늘해랑' : '포트빌'; // 기타 타입은 포트빌로 처리

          const calculation = this.calculateRoomBillingWithAdjustedFund(
            usageRecord,
            settings,
            buildingUsageRecord,
            totalElectricityUsage,
            roomSubType === '늘해랑' ? neulhaerangTotalWaterUsage : portvilleTotalWaterUsage,
            Math.max(0, adjustedElectricityFund), // 음수 방지
            roomSubType
          );
          results.push(calculation);
          roomCount++;
          logger.debug(`일반 호실 ${usageRecord.roomNumber} (${roomSubType}) 전력기금: ${calculation.electricity.fund}`);
        }
      }

      // logger.debug(`2단계 완료: 일반 호실 ${roomCount}개 계산`);
      // logger.debug(`최종 결과 수: ${results.length}`);

      return results;
    } catch (error) {
      logger.error('호실별 요금 계산 실패:', error);
      throw new BillingError('호실별 요금 계산에 실패했습니다.', 'CALCULATE_ROOM_BILLING_ERROR', error);
    }
  }

  /**
   * 시설물 청구 계산
   */
  private calculateFacilityBilling(
    usageRecord: PortvilleUsageRecord,
    settings: PortvilleBillingSettings,
    buildingUsageRecord: PortvilleBuildingUsageRecord,
    totalElectricityUsage: number,
    totalWaterUsage: number
  ): RoomBillingCalculation {
    // 시설물은 면적기여비율 0 (공동사용량 및 기본요금 제외)
    const areaContributionRatio = 0;
        
    // 개별 사용량 비율 계산
    const electricityIndividualRatio = totalElectricityUsage > 0 
      ? (usageRecord.electricityIndividualUsage || 0) / buildingUsageRecord.buildingIndividualUsage
      : 0;
    
    const waterIndividualRatio = totalWaterUsage > 0 
      ? (usageRecord.waterIndividualUsage || 0) / buildingUsageRecord.buildingWaterIndividualUsage
      : 0;

    // 시설물 전기 요금 계산 (기본요금과 공동요금 제외)
    const electricityBaseFee = 0;
    const electricityCommonFee = 0;
    
    const electricityIndividualRatio2 = totalElectricityUsage > 0 
      ? buildingUsageRecord.buildingIndividualUsage / totalElectricityUsage
      : 0;
    const electricityIndividualFeeRaw = buildingUsageRecord.totalElectricityFee * electricityIndividualRatio2 * electricityIndividualRatio;
    const electricityIndividualFee = roundElectricityFee(electricityIndividualFeeRaw);
    
    const electricityTotalFee = electricityIndividualFee;
    
    // 시설물 전력기금: (전력량요금 + 부가세) * 전력기금 비율, 반올림 처리
    const electricityFeeWithVAT = electricityIndividualFee * VAT_RATES.ELECTRICITY_FUND_VAT;
    const electricityFundRaw = electricityFeeWithVAT * ELECTRICITY_FUND.FACILITY_RATE;
    const electricityFund = roundElectricityFund(electricityFundRaw);
    const electricityOffsetFee = 0; // 시설물은 상계처리비 없음

    // 수도 요금 계산
    const waterIndividualRatio2 = totalWaterUsage > 0 
      ? buildingUsageRecord.buildingWaterIndividualUsage / totalWaterUsage
      : 0;
    const waterIndividualFeeRaw = buildingUsageRecord.totalWaterFee * waterIndividualRatio2 * waterIndividualRatio;
    const waterCommonFee = 0; // 시설물은 공동수도료 없음
    
    // logger.debug(`시설물 ${usageRecord.roomNumber} 수도료 계산 (올림 전):`, {
    //   waterIndividualFeeRaw: waterIndividualFeeRaw,
    //   waterCommonFeeRaw: 0
    // });

    const waterIndividualFee = roundWaterFee(waterIndividualFeeRaw);
    const waterTotalFee = waterIndividualFee;

    // logger.debug(`시설물 ${usageRecord.roomNumber} 수도료 계산 (올림 후):`, {
    //   waterIndividualFee: waterIndividualFee,
    //   waterCommonFee: waterCommonFee,
    //   waterTotalFee: waterTotalFee
    // });

    // 시설물은 공용 사용량 0
    const electricityCommonUsage = 0;
    const waterCommonUsage = 0;

    // 시설물명 조회 (입주자 정보 대신)
    const facilityType = settings.roomSettings?.roomType || '시설물';
    const residentName = `${facilityType}`;

    return {
      roomId: usageRecord.roomId,
      roomNumber: usageRecord.roomNumber,
      residentName,
      electricity: {
        baseFee: electricityBaseFee,
        commonFee: electricityCommonFee,
        individualFee: electricityIndividualFee,
        totalFee: electricityTotalFee,
        fund: electricityFund,
        offsetFee: electricityOffsetFee
      },
      water: {
        individualFee: waterIndividualFee,
        commonFee: waterCommonFee,
        totalFee: waterTotalFee
      },
      usage: {
        electricityIndividual: usageRecord.electricityIndividualUsage || 0,
        electricityCommon: electricityCommonUsage,
        waterIndividual: usageRecord.waterIndividualUsage || 0,
        waterCommon: waterCommonUsage
      },
      ratios: {
        electricityAreaContribution: areaContributionRatio,
        waterAreaContribution: areaContributionRatio,
        electricityIndividual: electricityIndividualRatio,
        waterIndividual: waterIndividualRatio
      }
    };
  }

  /**
   * 호실 청구 계산 (조정된 전력기금 사용)
   */
  private calculateRoomBillingWithAdjustedFund(
    usageRecord: PortvilleUsageRecord,
    settings: PortvilleBillingSettings,
    buildingUsageRecord: PortvilleBuildingUsageRecord,
    totalElectricityUsage: number,
    totalWaterUsage: number,
    adjustedElectricityFund: number,
    roomSubType: '포트빌' | '늘해랑' = '포트빌'
  ): RoomBillingCalculation {
    // 호실의 면적기여비율 (전기/수도 분리)
    logger.debug(`호실 ${usageRecord.roomNumber} 면적기여비율 로드:`);
    logger.debug(`- settings.roomSettings:`, settings.roomSettings);
    logger.debug(`- electricityAreaContributionRatio (raw):`, settings.roomSettings?.electricityAreaContributionRatio);
    logger.debug(`- waterAreaContributionRatio (raw):`, settings.roomSettings?.waterAreaContributionRatio);
    logger.debug(`- 백워드 호환 areaContributionRatio:`, (settings.roomSettings as any)?.areaContributionRatio);
    
    const electricityAreaContributionRatio = settings.roomSettings?.electricityAreaContributionRatio !== undefined ? settings.roomSettings.electricityAreaContributionRatio : ((settings.roomSettings as any)?.areaContributionRatio !== undefined ? (settings.roomSettings as any).areaContributionRatio : AREA_CONTRIBUTION_RATIOS.ELECTRICITY_DEFAULT);
    const waterAreaContributionRatio = settings.roomSettings?.waterAreaContributionRatio !== undefined ? settings.roomSettings.waterAreaContributionRatio : ((settings.roomSettings as any)?.areaContributionRatio !== undefined ? (settings.roomSettings as any).areaContributionRatio : AREA_CONTRIBUTION_RATIOS.WATER_DEFAULT);
    
    logger.debug(`- 최종 electricityAreaContributionRatio:`, electricityAreaContributionRatio);
    logger.debug(`- 최종 waterAreaContributionRatio:`, waterAreaContributionRatio);
    
    // 개별 사용량 비율 계산
    const electricityIndividualRatio = totalElectricityUsage > 0 
      ? (usageRecord.electricityIndividualUsage || 0) / buildingUsageRecord.buildingIndividualUsage
      : 0;
    
    const waterIndividualRatio = totalWaterUsage > 0 
      ? (usageRecord.waterIndividualUsage || 0) / buildingUsageRecord.buildingWaterIndividualUsage
      : 0;

    // 전기 요금 계산
    const electricityBaseFeeRaw = buildingUsageRecord.totalBaseFee * electricityAreaContributionRatio;
    
    const electricityCommonRatio = totalElectricityUsage > 0 
      ? buildingUsageRecord.buildingCommonUsage / totalElectricityUsage
      : 0;
    const electricityCommonFeeRaw = buildingUsageRecord.totalElectricityFee * electricityCommonRatio * electricityAreaContributionRatio;
    
    const electricityIndividualRatio2 = totalElectricityUsage > 0 
      ? buildingUsageRecord.buildingIndividualUsage / totalElectricityUsage
      : 0;
    const electricityIndividualFeeRaw = buildingUsageRecord.totalElectricityFee * electricityIndividualRatio2 * electricityIndividualRatio;
    
    // 전기요금 반올림 (100원 단위)
    const electricityBaseFee = roundElectricityFee(electricityBaseFeeRaw);
    const electricityCommonFee = roundElectricityFee(electricityCommonFeeRaw);
    const electricityIndividualFee = roundElectricityFee(electricityIndividualFeeRaw);
    
    const electricityTotalFee = electricityBaseFee + electricityCommonFee + electricityIndividualFee;
    
    // 호실 전력기금: (조정된 건물 전력기금 - 시설물 전력기금) * 면적기여비율
    const electricityFundRaw = adjustedElectricityFund * electricityAreaContributionRatio;
    const electricityFund = roundElectricityFund(electricityFundRaw);

    // logger.debug(`호실 ${usageRecord.roomNumber} 전력기금 계산 (반올림 전):`, {
    //   electricityFundRaw: electricityFundRaw,
    //   adjustedElectricityFund: adjustedElectricityFund,
    //   electricityAreaContributionRatio: electricityAreaContributionRatio
    // });
    
    // 상계처리비
    const electricityOffsetFeeRaw = buildingUsageRecord.totalOffsetFee * electricityAreaContributionRatio;
    const electricityOffsetFee = roundOffsetFee(electricityOffsetFeeRaw);

    // 수도 요금 계산 - 호실 타입에 따라 다른 데이터 사용
    const isNeulhaerang = roomSubType === '늘해랑';
    const roomWaterFee = isNeulhaerang ? (buildingUsageRecord.neulhaerangTotalWaterFee || 0) : (buildingUsageRecord.portvilleTotalWaterFee || buildingUsageRecord.totalWaterFee);
    const roomWaterIndividualUsage = isNeulhaerang ? (buildingUsageRecord.neulhaerangBuildingWaterIndividualUsage || 0) : (buildingUsageRecord.portvilleBuildingWaterIndividualUsage || buildingUsageRecord.buildingWaterIndividualUsage);
    const roomWaterCommonUsage = isNeulhaerang ? (buildingUsageRecord.neulhaerangBuildingWaterCommonUsage || 0) : (buildingUsageRecord.portvilleBuildingWaterCommonUsage || buildingUsageRecord.buildingWaterCommonUsage);

    // 늘해랑 호실인 경우 상세한 계산 과정 출력
    if (isNeulhaerang) {
      console.log(`\n=== 늘해랑 호실 ${usageRecord.roomNumber} 수도요금 계산 상세 ===`);
      console.log('1. 기본 데이터:', {
        roomWaterFee: `${roomWaterFee.toLocaleString()}원`,
        roomWaterIndividualUsage: `${roomWaterIndividualUsage}톤`,
        roomWaterCommonUsage: `${roomWaterCommonUsage}톤`,
        totalWaterUsage: `${totalWaterUsage}톤`,
        호실개별사용량: `${usageRecord.waterIndividualUsage || 0}톤`,
        면적기여비율: waterAreaContributionRatio
      });
    }

    const waterIndividualRatio2 = totalWaterUsage > 0
      ? roomWaterIndividualUsage / totalWaterUsage
      : 0;
    const waterIndividualFeeRaw = roomWaterFee * waterIndividualRatio2 * waterIndividualRatio;

    const waterCommonRatio = totalWaterUsage > 0
      ? roomWaterCommonUsage / totalWaterUsage
      : 0;
    const waterCommonFeeRaw = roomWaterFee * waterCommonRatio * waterAreaContributionRatio;

    // 늘해랑 호실인 경우 계산 과정 상세 출력
    if (isNeulhaerang) {
      console.log('2. 비율 계산:', {
        waterIndividualRatio: `${waterIndividualRatio.toFixed(6)} (호실개별사용량 / 건물개별사용량)`,
        waterIndividualRatio2: `${waterIndividualRatio2.toFixed(6)} (건물개별사용량 / 전체사용량)`,
        waterCommonRatio: `${waterCommonRatio.toFixed(6)} (건물공용사용량 / 전체사용량)`
      });

      console.log('3. 반올림/올림 처리 전 원시 계산값:', {
        waterIndividualFeeRaw: `${waterIndividualFeeRaw.toFixed(2)}원`,
        waterIndividualFee계산식: `${roomWaterFee} × ${waterIndividualRatio2.toFixed(6)} × ${waterIndividualRatio.toFixed(6)}`,
        waterCommonFeeRaw: `${waterCommonFeeRaw.toFixed(2)}원`,
        waterCommonFee계산식: `${roomWaterFee} × ${waterCommonRatio.toFixed(6)} × ${waterAreaContributionRatio}`
      });
    }
    
    // logger.debug(`호실 ${usageRecord.roomNumber} 수도료 계산 (올림 전):`, {
    //   waterIndividualFeeRaw: waterIndividualFeeRaw,
    //   waterCommonFeeRaw: waterCommonFeeRaw,
    //   waterAreaContributionRatio: waterAreaContributionRatio,
    //   waterCommonRatio: waterCommonRatio
    // });
    
    // 수도료 올림 처리 (10원 단위, 음수 처리 포함)
    const waterIndividualFee = roundWaterFee(waterIndividualFeeRaw);
    const waterCommonFee = roundWaterFee(waterCommonFeeRaw);

    const waterTotalFee = waterIndividualFee + waterCommonFee;

    // 늘해랑 호실인 경우 최종 결과 출력
    if (isNeulhaerang) {
      console.log('4. 올림 처리 후 최종 결과:', {
        waterIndividualFee: `${waterIndividualFee.toLocaleString()}원`,
        waterCommonFee: `${waterCommonFee.toLocaleString()}원`,
        waterTotalFee: `${waterTotalFee.toLocaleString()}원`
      });
      console.log('=== 늘해랑 호실 계산 완료 ===\n');
    }
    
    // logger.debug(`호실 ${usageRecord.roomNumber} 수도료 계산 (올림 후):`, {
    //   waterIndividualFee: waterIndividualFee,
    //   waterCommonFee: waterCommonFee,
    //   waterTotalFee: waterTotalFee
    // });

    // 공용 사용량 계산 (면적기여비율 기반, 소숫점 둘째자리까지)
    const electricityCommonUsage = Math.round((buildingUsageRecord.buildingCommonUsage * electricityAreaContributionRatio) * ROUNDING_UNITS.DECIMAL_PLACES) / ROUNDING_UNITS.DECIMAL_PLACES;
    const waterCommonUsage = Math.round((roomWaterCommonUsage * waterAreaContributionRatio) * ROUNDING_UNITS.DECIMAL_PLACES) / ROUNDING_UNITS.DECIMAL_PLACES;

    // 입주자 정보 조회
    const residentName = '입주자 정보 없음'; // TODO: 실제 입주자 정보 조회

    return {
      roomId: usageRecord.roomId,
      roomNumber: usageRecord.roomNumber,
      residentName,
      electricity: {
        baseFee: electricityBaseFee,
        commonFee: electricityCommonFee,
        individualFee: electricityIndividualFee,
        totalFee: electricityTotalFee,
        fund: electricityFund,
        offsetFee: electricityOffsetFee
      },
      water: {
        individualFee: waterIndividualFee,
        commonFee: waterCommonFee,
        totalFee: waterTotalFee
      },
      usage: {
        electricityIndividual: usageRecord.electricityIndividualUsage || 0,
        electricityCommon: electricityCommonUsage,
        waterIndividual: usageRecord.waterIndividualUsage || 0,
        waterCommon: waterCommonUsage
      },
      ratios: {
        electricityAreaContribution: electricityAreaContributionRatio,
        waterAreaContribution: waterAreaContributionRatio,
        electricityIndividual: electricityIndividualRatio,
        waterIndividual: waterIndividualRatio
      }
    };
  }

  /**
   * 호실별 요금 계산 결과를 저장합니다.
   * 기존 결과가 있으면 업데이트하고, 없으면 새로 생성합니다.
   */
  async saveBillingCalculations(
    year: number,
    month: number,
    calculations: RoomBillingCalculation[],
    buildingUsageRecordId: string
  ): Promise<void> {
    try {
      // 기존 계산 결과 조회
      const existingCalculations = await this.getBillingCalculations(year, month);
      const existingCalculationsMap = new Map(
        existingCalculations.map(calc => [calc.roomId, calc])
      );

      const batch = writeBatch(db);
      const now = Timestamp.now();

      for (const calc of calculations) {
        const calculationData: Omit<PortvilleBillingCalculation, 'id'> = {
          year,
          month,
          roomId: calc.roomId,
          roomNumber: calc.roomNumber,
          residentName: calc.residentName,
          electricity: calc.electricity,
          water: calc.water,
          usage: calc.usage,
          ratios: {
            areaContribution: calc.ratios.electricityAreaContribution, // 백워드 호환성을 위해 전기 면적기여비율을 기본값으로 사용
            electricityIndividual: calc.ratios.electricityIndividual,
            waterIndividual: calc.ratios.waterIndividual
          } as any,
          createdAt: now.toDate(),
          updatedAt: now.toDate(),
          buildingUsageRecordId
        };

        const existingCalc = existingCalculationsMap.get(calc.roomId);
        
        if (existingCalc) {
          // 기존 결과가 있으면 업데이트
          const docRef = doc(db, 'portville_billing_calculations', existingCalc.id);
          batch.update(docRef, {
            ...calculationData,
            createdAt: existingCalc.createdAt, // 생성일은 유지
            updatedAt: now.toDate()
          });
        } else {
          // 기존 결과가 없으면 새로 생성
          const docRef = doc(collection(db, 'portville_billing_calculations'));
          batch.set(docRef, calculationData);
        }
      }

      await batch.commit();
      logger.debug(`호실별 요금 계산 결과 저장 완료: ${calculations.length}개 호실`);
    } catch (error) {
      logger.error('호실별 요금 계산 결과 저장 실패:', error);
      throw new BillingError('호실별 요금 계산 결과 저장에 실패했습니다.', 'SAVE_BILLING_CALCULATIONS_ERROR', error);
    }
  }

  /**
   * 특정 연월의 호실별 요금 계산 결과를 조회합니다.
   */
  async getBillingCalculations(year: number, month: number): Promise<PortvilleBillingCalculation[]> {
    try {
      const q = query(
        collection(db, 'portville_billing_calculations'),
        where('year', '==', year),
        where('month', '==', month),
        orderBy('roomNumber')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PortvilleBillingCalculation));
    } catch (error) {
      logger.error('호실별 요금 계산 결과 조회 실패:', error);
      throw new BillingError('호실별 요금 계산 결과 조회에 실패했습니다.', 'GET_BILLING_CALCULATIONS_ERROR', error);
    }
  }

  /**
   * 특정 연월의 호실별 요금 계산 결과를 삭제합니다.
   */
  async deleteBillingCalculations(year: number, month: number): Promise<void> {
    try {
      const q = query(
        collection(db, 'portville_billing_calculations'),
        where('year', '==', year),
        where('month', '==', month)
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);

      snapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      logger.debug(`호실별 요금 계산 결과 삭제 완료: ${year}년 ${month}월`);
    } catch (error) {
      logger.error('호실별 요금 계산 결과 삭제 실패:', error);
      throw new BillingError('호실별 요금 계산 결과 삭제에 실패했습니다.', 'DELETE_BILLING_CALCULATIONS_ERROR', error);
    }
  }

  /**
   * 호실별 요금 계산 결과를 청구서에 반영합니다.
   */
  async applyCalculationsToBills(year: number, month: number): Promise<string[]> {
    try {
      // 계산 결과 조회
      const calculations = await this.getBillingCalculations(year, month);
      if (calculations.length === 0) {
        throw new BillingError('저장된 계산 결과가 없습니다.', 'NO_CALCULATIONS_ERROR');
      }

      // 청구서 생성 요청
      const billGenerationRequest: BillGenerationRequest = {
        roomIds: calculations.map(calc => calc.roomId),
        billingPeriod: {
          year,
          month
        },
        issuedDate: new Date()
      };

      // 청구서 생성
      const billIds = await this.generateBills(billGenerationRequest);
      
      logger.debug(`계산 결과를 청구서에 반영 완료: ${billIds.length}개 청구서`);
      return billIds;
    } catch (error) {
      logger.error('계산 결과를 청구서에 반영 실패:', error);
      throw new BillingError('계산 결과를 청구서에 반영하는데 실패했습니다.', 'APPLY_CALCULATIONS_TO_BILLS_ERROR', error);
    }
  }

  // ==================== 입주자용 조회 기능 ====================

  /**
   * UID로 청구서 조회 (입주자용) - 최적화된 버전
   */
  async getBillsByUid(uid: string, year?: number, month?: number): Promise<PortvilleBill[]> {
    try {
      logger.startTimer(`getBillsByUid-${uid}`);
      logger.debug(`UID 청구서 조회 시작: uid=${uid}, year=${year}, month=${month}`);
      
      let q = query(
        collection(db, 'portville_bills'),
        where('residentUid', '==', uid),
        orderBy('billingPeriod.year', 'desc'),
        orderBy('billingPeriod.month', 'desc')
      );

      // 연/월 필터 적용
      if (year !== undefined) {
        q = query(q, where('billingPeriod.year', '==', year));
        if (month !== undefined) {
          q = query(q, where('billingPeriod.month', '==', month));
        }
      }

      const snapshot = await getDocs(q);
      logger.debug(`UID 청구서 조회 완료: ${snapshot.docs.length}개 문서`);
      
      // 🚀 성능 최적화: 공통 변환 함수 사용
      const bills = snapshot.docs.map(transformBillDocument);
      
      logger.endTimer(`getBillsByUid-${uid}`);
      return bills;
    } catch (error) {
      logger.error('UID로 청구서 조회 실패:', error);
      throw new BillingError('청구서 조회에 실패했습니다.', 'GET_BILLS_BY_UID_ERROR', error);
    }
  }

  /**
   * 호실번호로 전화번호 조회 (SMS 인증용) - 청구서에서 조회
   */
  async getRoomPhoneNumber(roomNumber: string, year: number, month: number): Promise<string | null> {
    try {
      // 해당 연/월의 청구서에서 전화번호 조회
      const billQuery = query(
        collection(db, 'portville_bills'),
        where('roomNumber', '==', roomNumber),
        where('billingPeriod.year', '==', year),
        where('billingPeriod.month', '==', month)
      );
      
      const billSnapshot = await getDocs(billQuery);
      
      if (billSnapshot.empty) {
        logger.debug(`${year}년 ${month}월 호실 ${roomNumber} 청구서 없음`);
        return null;
      }

      const billDoc = billSnapshot.docs[0];
      const billData = billDoc.data();
      
      // 청구서의 입주자 정보에서 전화번호 조회
      if (billData.residentInfo && billData.residentInfo.phoneNumber) {
        const phoneNumber = billData.residentInfo.phoneNumber;
        logger.debug(`${year}년 ${month}월 호실 ${roomNumber} 전화번호 조회: ${phoneNumber}`);
        return phoneNumber;
      }
      
      logger.debug(`${year}년 ${month}월 호실 ${roomNumber} 청구서에 전화번호 정보 없음`);
      return null;
    } catch (error) {
      logger.error('청구서에서 전화번호 조회 실패:', error);
      
      // CORS 오류 처리
      if (error instanceof Error && error.message.includes('access control checks')) {
        logger.warn('CORS 오류가 발생했습니다. Firebase 콘솔에서 도메인을 등록해주세요.');
        throw new BillingError('도메인 인증이 필요합니다. 관리자에게 문의해주세요.', 'CORS_ERROR', error);
      }
      
      throw new BillingError('전화번호 조회에 실패했습니다.', 'GET_ROOM_PHONE_ERROR', error);
    }
  }

  /**
   * 호실번호로 청구서 조회 (SMS 인증용) - 최적화된 버전
   */
  async getBillsByRoomNumber(roomNumber: string, year?: number, month?: number): Promise<PortvilleBill[]> {
    try {
      logger.startTimer(`getBillsByRoomNumber-${roomNumber}`);
      logger.debug(`호실번호 청구서 조회 시작: roomNumber=${roomNumber}, year=${year}, month=${month}`);
      
      let q = query(
        collection(db, 'portville_bills'),
        where('roomNumber', '==', roomNumber),
        orderBy('billingPeriod.year', 'desc'),
        orderBy('billingPeriod.month', 'desc')
      );

      // 연/월 필터 적용
      if (year !== undefined) {
        q = query(q, where('billingPeriod.year', '==', year));
        if (month !== undefined) {
          q = query(q, where('billingPeriod.month', '==', month));
        }
      }

      const snapshot = await getDocs(q);
      logger.debug(`호실번호 청구서 조회 완료: ${roomNumber}, ${snapshot.docs.length}개`);
      
      // 🚀 성능 최적화: 공통 변환 함수 사용
      const bills = snapshot.docs.map(transformBillDocument);
      
      logger.endTimer(`getBillsByRoomNumber-${roomNumber}`);
      return bills;
    } catch (error) {
      logger.error('호실번호로 청구서 조회 실패:', error);
      
      // CORS 오류 처리
      if (error instanceof Error && error.message.includes('access control checks')) {
        logger.warn('CORS 오류가 발생했습니다. Firebase 콘솔에서 도메인을 등록해주세요.');
        throw new BillingError('도메인 인증이 필요합니다. 관리자에게 문의해주세요.', 'CORS_ERROR', error);
      }
      
      throw new BillingError('청구서 조회에 실패했습니다.', 'GET_BILLS_BY_ROOM_NUMBER_ERROR', error);
    }
  }
}

// 싱글톤 인스턴스 내보내기
export const portvilleBillingService = PortvilleBillingService.getInstance();

